import { Spinner } from "@chakra-ui/react";
import React from "react";
import styled from "styled-components";

import { Button } from "../../Button";

/* ------------------------------------------------------------------ */
/* Styled Button                                                       */
/* ------------------------------------------------------------------ */

interface ButtonStyledProps {
	fontSize?: string;
	marginBottom?: string;
	marginTop?: string;
	widthExpand?: boolean;
}

const ButtonStyled = styled(Button)<ButtonStyledProps>`
	align-self: flex-start;
	position: relative;
	height: 60px;
	font-size: ${({ fontSize }) => fontSize ?? "inherit"} !important;
	margin-top: ${({ marginTop }) => marginTop ?? "0"};
	margin-bottom: ${({ marginBottom }) => marginBottom ?? "0"};
	width: ${({ widthExpand }) => (widthExpand ? "100%" : "auto")};

	&:hover {
		background-color: #0031dd;
		border-color: #0031dd;
	}

	&:focus-visible {
		background-color: #003ce9;
		border-color: #003ce9;
		outline: 3px solid rgba(0, 60, 233, 0.4);
		outline-offset: 2px;
	}
`;

/* ------------------------------------------------------------------ */
/* Props                                                              */
/* ------------------------------------------------------------------ */

export interface SubmitProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
	disabled?: boolean;
	fontSize?: string;
	isSubmitting?: boolean;
	marginBottom?: string;
	marginTop?: string;
	refineResults?: boolean;
	spinner?: boolean;
	submittingText?: React.ReactNode;
	testId?: string;
	text?: React.ReactNode;
	type?: "button" | "submit" | "reset";
	widthExpand?: boolean;
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

export const Submit: React.FC<SubmitProps> = ({
	type = "submit",
	text,
	submittingText = "Submitting...",
	isSubmitting = false,
	spinner = false,
	marginTop,
	marginBottom,
	fontSize,
	widthExpand,
	refineResults,
	disabled,
	testId = "submit-button",
	...props
}) => {
	const isDisabled = isSubmitting || disabled;

	return (
		// @ts-ignore
		<ButtonStyled
			type={type}
			marginTop={marginTop}
			marginBottom={marginBottom}
			fontSize={fontSize}
			widthExpand={widthExpand}
			disabled={isDisabled}
			disablePointer={isDisabled}
			disabledStyle={isDisabled}
			refineResults={refineResults}
			aria-busy={isSubmitting}
			aria-disabled={isDisabled}
			data-testid={testId}
			{...props}
		>
			{isSubmitting ? (
				spinner ? (
					<Spinner size="sm" />
				) : (
					submittingText
				)
			) : (
				(text ?? "Submit")
			)}
		</ButtonStyled>
	);
};
